// hooks/useParallaxHoverSplit.js
import { useRef, useCallback, useEffect } from "react";

export function useParallaxHoverSplit({
  maxShift = 12, // px translate range
  maxTilt = 6, // deg tilt range
  spring = 0.15, // smoothing (0..1)
  applyTilt = true, // toggle tilt
  disabled = false, // force off
  disableOnTouch = true, // auto-disable on touch devices
} = {}) {
  const containerEl = useRef(null);
  const targetEl = useRef(null);

  // function refs for robustness with polymorphic components
  const containerRef = useCallback((node) => {
    containerEl.current = node;
  }, []);
  const targetRef = useCallback((node) => {
    targetEl.current = node;
  }, []);

  useEffect(() => {
    const container = containerEl.current;
    const target = targetEl.current;

    if (!container || !target) return;
    if (disabled) return;
    if (
      disableOnTouch &&
      ("ontouchstart" in window || navigator.maxTouchPoints > 0)
    )
      return;

    let targetTx = 0,
      targetTy = 0,
      targetRx = 0,
      targetRy = 0;
    let tx = 0,
      ty = 0,
      rx = 0,
      ry = 0;
    let rafId;

    const lerp = (a, b, t) => a + (b - a) * t;

    const onMove = (e) => {
      const r = container.getBoundingClientRect();
      const nx = (e.clientX - r.left) / r.width - 0.5; // -0.5..0.5
      const ny = (e.clientY - r.top) / r.height - 0.5; // -0.5..0.5

      targetTx = nx * maxShift * 2;
      targetTy = ny * maxShift * 2;

      if (applyTilt) {
        targetRy = -nx * maxTilt * 2; // rotateY left/right
        targetRx = ny * maxTilt * 2; // rotateX up/down
      } else {
        targetRy = targetRx = 0;
      }
    };

    const onLeave = () => {
      targetTx = targetTy = targetRx = targetRy = 0;
    };

    const animate = () => {
      tx = lerp(tx, targetTx, spring);
      ty = lerp(ty, targetTy, spring);
      rx = lerp(rx, targetRx, spring);
      ry = lerp(ry, targetRy, spring);
      target.style.transform = `translate(${tx}px, ${ty}px) rotateX(${rx}deg) rotateY(${ry}deg)`;
      rafId = requestAnimationFrame(animate);
    };
    animate();

    container.addEventListener("mousemove", onMove, { passive: true });
    container.addEventListener("mouseleave", onLeave, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseleave", onLeave);
      // reset transform on cleanup
      if (target) target.style.transform = "";
    };
  }, [maxShift, maxTilt, spring, applyTilt, disabled, disableOnTouch]);

  return { containerRef, targetRef };
}
